import { lazy, Suspense, useEffect, useState } from "react";
import { getCartTotal } from "../header-service";
import { CartTotal, ShoppingCartType } from "../types";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { publishCartData, publishCartState } from "../../../redux/features/cart/cartSlice";
import { AddTicketsData } from "../../../common";
import { getCartData } from "../header-service";

// Lazy: the popover (incl. gsap, rc-slider, prize extensions) is only needed once the cart opens.
// Keeps ~300 KiB of source out of the initial header bundle.
const ShoppingCartPopover = lazy(() => import("./shoppingCartPopover"));

const ShoppingCart = () => {
  const dispatch = useAppDispatch();
  const [cartTotalData, setCartTotalData] = useState<CartTotal>();
  const isCartOpen = useAppSelector(state => state.cart.isCartOpen);
  const cartData = useAppSelector(state => state.cart.cartData);

  useEffect(() => {
    // Call GetCartTotal on page load
    getItemCountInBasket();
  }, []);

  useEffect(() => {
    if (isCartOpen) {
      // Call fetchCartData on shopping cart opened
      fetchCartData();
    } else {
      // Remove cartData, so the next cart open will fetch new data instead of showing old content
      dispatch(publishCartData({ cartData: null }));
    }
  }, [isCartOpen]);

  useEffect(() => {
    // Call GetCartTotal after fetchCartData is done
    if (cartData !== null) {
      getItemCountInBasket();
    }
  }, [cartData]);

  useEffect(() => {
    window.addEventListener("addToBasketApiEvent", addToBasketApiEventHandler);
    return () => window.removeEventListener("addToBasketApiEvent", addToBasketApiEventHandler);
  }, []);

  // Below event listener will get removed once we are fully migrated to React.
  // It is registered to handle those requests where user is adding tickets to the basket from
  // Angular component.
  const addToBasketApiEventHandler = (e: CustomEvent<{ addToBasketApiData: string; prizeId: number }>) => {
    const addToBasketApiCartData = JSON.parse(e.detail.addToBasketApiData) as AddTicketsData;
    if (addToBasketApiCartData.AddTicketStatusResult === 1 || addToBasketApiCartData.AddTicketStatusResult === 5) {
      addToBasketApiCartData.Cart.CartItems = addToBasketApiCartData.Cart.CartItems.sort(
        (a, b) => a.CompetitionPictureTypeId - b.CompetitionPictureTypeId || a.CompetitionId - b.CompetitionId
      );
      dispatch(publishCartData({ cartData: addToBasketApiCartData.Cart }));
      dispatch(publishCartState({ isOpen: true }));
    }
  };

  const getItemCountInBasket = async () => {
    const response = await getCartTotal();
    const data = (await response.json()) as CartTotal;
    setCartTotalData(data);
  };

  const fetchCartData = async () => {
    try {
      const response = await getCartData(true);
      const cartDetails = (await response.json()) as ShoppingCartType;
      cartDetails.CartItems = cartDetails.CartItems.sort(
        (a, b) => a.CompetitionPictureTypeId - b.CompetitionPictureTypeId || a.CompetitionId - b.CompetitionId
      );
      dispatch(publishCartData({ cartData: cartDetails }));
    } catch (error) {
      console.error(`Error while fetching cart details - ${JSON.stringify(error)}`);
    }
  };

  return (
    <>
      <div
        onClick={() => {
          dispatch(publishCartState({ isOpen: !isCartOpen }));
        }}
        className="cursor-pointer relative"
        id="user-shopping-cart">
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="29" viewBox="0 0 24 26" fill="none">
          <path
            d="M5.99805 17.4805C6.52849 17.4805 7.03719 17.6912 7.41226 18.0662C7.78734 18.4414 7.99805 18.95 7.99805 19.4804C7.99805 20.011 7.78734 20.5196 7.41226 20.8946C7.03719 21.2698 6.52849 21.4805 5.99805 21.4805C5.46761 21.4805 4.95891 21.2698 4.58384 20.8946C4.20877 20.5196 3.99805 20.011 3.99805 19.4804C3.99805 18.95 4.20877 18.4414 4.58384 18.0662C4.95891 17.6912 5.46761 17.4805 5.99805 17.4805ZM5.99805 17.4805H16.998M5.99805 17.4805V3.48047H3.99805M16.998 17.4805C17.5284 17.4805 18.0372 17.6912 18.4122 18.0662C18.7874 18.4414 18.9981 18.95 18.9981 19.4804C18.9981 20.011 18.7874 20.5196 18.4122 20.8946C18.0372 21.2698 17.5284 21.4805 16.998 21.4805C16.4676 21.4805 15.959 21.2698 15.5838 20.8946C15.2087 20.5196 14.9981 20.011 14.9981 19.4804C14.9981 18.95 15.2087 18.4414 15.5838 18.0662C15.959 17.6912 16.4676 17.4805 16.998 17.4805ZM5.99805 5.48047L19.998 6.48047L18.9981 13.4804H5.99805"
            stroke="#696971"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="bg-[#f33424] text-white rounded-xl border-1 border-solid bolder-[#f33424] absolute top-0 right-0 text-center text-[10px] px-1 h-3.5 min-w-3.5 lg:w-auto lg:min-w-4 lg:h-4 lg:pt-[2px] lg:top-[-4px] lg:right-[-6px]">
          {cartTotalData?.TotalTicketCount}
        </span>
      </div>
      {isCartOpen && (
        <Suspense fallback={null}>
          <ShoppingCartPopover />
        </Suspense>
      )}
    </>
  );
};

export default ShoppingCart;
